export const GRADE_COLORS = {
  excellent: "#92D050",
  good: "#FFFF00",
  fair: "#57A3CD",
  poor: "#E95A57",
};

export const GRADE_RANGE_LEGEND = [
  { color: GRADE_COLORS.excellent, label: "> 90" },
  { color: GRADE_COLORS.good, label: "80 - 90" },
  { color: GRADE_COLORS.fair, label: "60 - 80" },
  { color: GRADE_COLORS.poor, label: "< 60" },
];

export const SCATTER_COLORS = {
  allSector: "#bdbdbd",
  ageComparable: "#57c8e8",
  vessel: "#1b3a57",
};

export const MAX_DISPLAY_SHIP_AGE = 60;
export const MAX_SCATTER_SHIP_AGE = 25;

const isInAgeRange = (age, range) =>
  age >= range.min && age < range.max;

const sanitizeShipAge = (age) => {
  if (age == null || Number.isNaN(age) || age < 0 || age > MAX_DISPLAY_SHIP_AGE) {
    return null;
  }

  return age;
};

const sanitizeScatterShipAge = (age) => {
  const sanitized = sanitizeShipAge(age);
  if (sanitized == null || sanitized >= MAX_SCATTER_SHIP_AGE) {
    return null;
  }

  return sanitized;
};

export const getGradeBarColor = (grade) => {
  if (grade > 90) return GRADE_COLORS.excellent;
  if (grade >= 80) return GRADE_COLORS.good;
  if (grade >= 60) return GRADE_COLORS.fair;
  return GRADE_COLORS.poor;
};

const floorToTen = (value) => Math.floor(value / 10) * 10;

const ceilToTen = (value) => Math.floor((value + 9) / 10) * 10;

const buildDynamicGradeScale = (grades = [], padding = 4) => {
  const validGrades = grades.filter((grade) => grade >= 0 && grade <= 100);

  if (validGrades.length === 0) {
    const tickValues = [];
    for (let tick = 0; tick <= 100; tick += 10) {
      tickValues.push(tick);
    }

    return {
      min: 0,
      max: 100,
      tickValues,
      gradeMin: 0,
      gradeMax: 100,
    };
  }

  let gradeMin = Math.max(0, Math.min(...validGrades) - padding);
  let gradeMax = Math.min(100, Math.max(...validGrades) + padding);
  gradeMin = floorToTen(gradeMin);
  gradeMax = ceilToTen(gradeMax);

  if (gradeMax - gradeMin < 30) {
    const mid = (gradeMin + gradeMax) / 2;
    gradeMin = Math.max(0, floorToTen(mid - 15));
    gradeMax = Math.min(100, ceilToTen(mid + 15));
  }

  const tickValues = [];
  for (let tick = gradeMin; tick <= gradeMax; tick += 10) {
    tickValues.push(tick);
  }

  return {
    min: gradeMin,
    max: gradeMax,
    tickValues,
    gradeMin,
    gradeMax,
  };
};

export const getHistogramPercentageScaleBounds = (histogramPoints = []) => {
  const DEFAULT_AXIS_MAX = 20;
  const DEFAULT_TICK_STEP = 2;
  const EXPANDED_TICK_STEP = 5;

  const percentages = histogramPoints
    .map((point) => point.y)
    .filter((value) => value != null && !Number.isNaN(value) && value > 0);

  const peakPercentage = percentages.length > 0 ? Math.max(...percentages) : 0;

  const axisMax = peakPercentage <= DEFAULT_AXIS_MAX
    ? DEFAULT_AXIS_MAX
    : Math.ceil(peakPercentage / EXPANDED_TICK_STEP) * EXPANDED_TICK_STEP;

  const tickStep = axisMax > DEFAULT_AXIS_MAX ? EXPANDED_TICK_STEP : DEFAULT_TICK_STEP;

  const tickValues = [];
  for (let tick = 0; tick <= axisMax; tick += tickStep) {
    tickValues.push(tick);
  }

  return {
    min: 0,
    max: axisMax,
    tickStep,
    tickValues,
  };
};

export const getHistogramScaleBounds = (
  histogramPoints = [],
  vesselGrade = 0,
  averageGrade = 0
) =>
  buildDynamicGradeScale(
    [
      ...histogramPoints.map((point) => point.x),
      vesselGrade,
      averageGrade,
    ],
    4
  );

export const getScatterGradeScaleBounds = (
  scatterData = {},
  vesselGrade = 0,
  scatterAverageGrade = 0
) => {
  const grades = [
    ...(scatterData.allSectorShips || []).map((point) => point.y),
    ...(scatterData.ageComparableShips || []).map((point) => point.y),
    scatterData.vesselPoint?.y,
    vesselGrade,
    scatterAverageGrade,
  ];

  return buildDynamicGradeScale(grades, 5);
};

export const getScatterAgeScaleBounds = () => {
  const tickValues = [0, 5, 10, 15, 20, MAX_SCATTER_SHIP_AGE];

  return {
    min: 0,
    max: MAX_SCATTER_SHIP_AGE,
    tickValues,
  };
};

export const formatScatterAgeTickLabel = (value) => {
  const rounded = Math.round(value);
  if (rounded === MAX_SCATTER_SHIP_AGE) {
    return "25+";
  }

  return String(rounded);
};

export const EMPTY_HISTOGRAM_DATA = {
  points: [],
  barColors: [],
};

export const EMPTY_SCATTER_DATA = {
  allSectorShips: [],
  ageComparableShips: [],
  vesselPoint: null,
  ageRange: { min: 0, max: 0 },
};

const mapScatterPoint = (point) => {
  const age = sanitizeScatterShipAge(point.Age ?? point.x);
  const grade = point.Grade ?? point.y;

  if (age == null || grade == null || Number.isNaN(grade)) {
    return null;
  }

  return { x: age, y: grade };
};

export const mapAheadGradeApiResponse = (apiData = {}) => {
  const distribution = (apiData.Distribution || [])
    .filter((bucket) => bucket.Percentage > 0)
    .map((bucket) => ({
      x: bucket.Grade,
      y: bucket.Percentage,
    }));

  const ageRange = {
    min: apiData.AgeBracketStart ?? 0,
    max: apiData.AgeBracketEnd ?? 0,
  };

  const sectorShips = (apiData.SectorShips || [])
    .map((point) => ({
      x: point.Age,
      y: point.Grade,
      isCurrentShip: point.IsCurrentShip,
    }))
    .map((point) => {
      const age = sanitizeScatterShipAge(point.x);
      if (age == null) {
        return null;
      }

      return { ...point, x: age };
    })
    .filter(Boolean);

  const vesselPointEntry = sectorShips.find((point) => point.isCurrentShip);
  const peerShips = sectorShips.filter((point) => !point.isCurrentShip);

  const ageComparableShips = (apiData.AgeComparableShips || [])
    .map(mapScatterPoint)
    .filter(Boolean);

  const hasAgeData = apiData.HasAgeData === true;
  const allSectorShips = hasAgeData
    ? peerShips.filter((point) => !isInAgeRange(point.x, ageRange))
    : peerShips;

  const sectorAverageGrade = Math.round(apiData.AverageGrade ?? 0);

  return {
    vesselGrade: apiData.YourGrade ?? 0,
    vesselAge: sanitizeScatterShipAge(apiData.ShipAge ?? -1) ?? -1,
    vesselTypeLabel: apiData.SectorName || "vessel",
    ageRangeLabel: apiData.AgeRangeLabel || "",
    averageGrade: sectorAverageGrade,
    scatterAverageGrade: sectorAverageGrade,
    histogramData: {
      points: distribution,
      barColors: distribution.map((point) => getGradeBarColor(point.x)),
      averageGrade: sectorAverageGrade,
      vesselGrade: apiData.YourGrade ?? 0,
    },
    scatterData: {
      allSectorShips,
      ageComparableShips: hasAgeData ? ageComparableShips : [],
      vesselPoint: vesselPointEntry
        ? { x: vesselPointEntry.x, y: vesselPointEntry.y }
        : null,
      ageRange,
    },
    hasSectorData: apiData.HasSectorData === true,
    hasAgeData,
  };
};

